import { useState, type KeyboardEvent } from 'react';
import { TextArea } from 'sharedUi/TextArea';

interface Props {
  onSend: (content: string) => void;
  disabled?: boolean;
}

/** Composer: Enter sends, Shift+Enter makes a newline. Locked while a turn runs. */
const MessageComposer = ({ onSend, disabled }: Props) => {
  const [value, setValue] = useState('');

  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text);
    setValue('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="shrink-0 border-t border-line bg-surface px-4 py-3">
      <div className="flex w-full items-end gap-2 rounded-2xl border border-line bg-white px-3 py-1.5 shadow-sm transition-colors focus-within:border-primary dark:bg-gray-900">
        <TextArea
          value={value}
          onChange={setValue}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder={disabled ? 'Waiting for the reply…' : 'Type a message…'}
          disabled={disabled}
          className="flex-1 resize-none border-0 bg-transparent shadow-none focus:ring-0"
          aria-label="Message"
        />
        <button type="button" onClick={submit} disabled={disabled || !value.trim()} aria-label="Send message"
          className="ui-press mb-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity disabled:cursor-not-allowed disabled:opacity-40">
          <i className={disabled ? 'pi pi-spin pi-spinner' : 'pi pi-send'} aria-hidden />
        </button>
      </div>
      <p className="mt-1 px-1 text-[11px] text-ink-muted">
        Enter to send · Shift+Enter for a new line
      </p>
    </div>
  );
};

export default MessageComposer;
